import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '@/utils/axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Textarea } from '@/components/ui/textarea';
import { 
  ArrowLeft, Search, Syringe, Calendar, User, CheckCircle2, 
  Clock, AlertCircle, XCircle, Plus, FileText, Download, 
  Building2, Stethoscope, Package, RefreshCw, Info, Filter
} from 'lucide-react';
import { toast } from 'sonner';
import DateInput from '@/components/DateInput';

// Status colors and icons
const statusConfig = {
  complete: { 
    color: 'bg-green-100 text-green-800 border-green-200', 
    icon: CheckCircle2, 
    label: 'Completa',
    badgeColor: 'bg-green-500'
  },
  in_progress: { 
    color: 'bg-blue-100 text-blue-800 border-blue-200', 
    icon: Clock, 
    label: 'Em andamento',
    badgeColor: 'bg-blue-500'
  },
  pending: { 
    color: 'bg-gray-100 text-gray-800 border-gray-200', 
    icon: XCircle, 
    label: 'Pendente',
    badgeColor: 'bg-gray-400'
  },
  overdue: { 
    color: 'bg-red-100 text-red-800 border-red-200', 
    icon: AlertCircle, 
    label: 'Atrasada',
    badgeColor: 'bg-red-500'
  }
};

export default function VaccinationCard() {
  const { patientId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [patient, setPatient] = useState(null);
  const [vaccinationCard, setVaccinationCard] = useState(null);
  const [vaccines, setVaccines] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterGroup, setFilterGroup] = useState('all');
  
  // Modal states
  const [showVaccineModal, setShowVaccineModal] = useState(false);
  const [selectedVaccine, setSelectedVaccine] = useState(null);
  const [showRegisterModal, setShowRegisterModal] = useState(false);
  
  // Form state for new vaccination
  const [formData, setFormData] = useState({
    vaccination_date: new Date().toISOString().split('T')[0],
    dose_number: 1,
    dose_name: '',
    lot_number: '',
    manufacturer: '',
    application_site: '',
    healthcare_facility: '',
    administered_by: '',
    observations: ''
  });

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [cardRes, vaccinesRes] = await Promise.all([
        axiosInstance.get(`/patients/${patientId}/vaccination-card`),
        axiosInstance.get('/vaccines')
      ]);
      setVaccinationCard(cardRes.data);
      setPatient(cardRes.data.patient);
      setVaccines(vaccinesRes.data);
      setLoading(false);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast.error('Erro ao carregar cartão vacinal');
      setLoading(false);
    }
  }, [patientId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Filter vaccines
  const filteredVaccines = vaccines.filter(vaccine => {
    const matchesSearch = searchTerm === '' || 
      vaccine.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (vaccine.abbreviation && vaccine.abbreviation.toLowerCase().includes(searchTerm.toLowerCase())) ||
      vaccine.diseases_prevented.some(d => d.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesType = filterType === 'all' || vaccine.type === filterType || 
      (filterType === 'AMBAS' && vaccine.type === 'AMBAS');
    
    const matchesGroup = filterGroup === 'all' || 
      vaccine.target_groups.includes(filterGroup);
    
    return matchesSearch && matchesType && matchesGroup;
  });

  // Filter vaccination status
  const filteredStatus = vaccinationCard?.vaccination_status?.filter(status => {
    const matchesSearch = searchTerm === '' || 
      status.vaccine_name.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesType = filterType === 'all' || status.type === filterType ||
      (filterType === 'AMBAS' && status.type === 'AMBAS');
    
    const matchesStatus = filterStatus === 'all' || status.status === filterStatus;
    
    return matchesSearch && matchesType && matchesStatus;
  }) || [];

  // Open register modal
  const handleSelectVaccine = (vaccine) => {
    setSelectedVaccine(vaccine);
    
    // Find vaccination status for this vaccine
    const status = vaccinationCard?.vaccination_status?.find(s => s.vaccine_id === vaccine.id);
    const nextDoseNumber = status ? status.doses_taken + 1 : 1;
    const nextDose = vaccine.doses.find(d => d.dose_number === nextDoseNumber);
    
    setFormData({
      vaccination_date: new Date().toISOString().split('T')[0],
      dose_number: nextDoseNumber,
      dose_name: nextDose?.dose_name || `${nextDoseNumber}ª dose`,
      lot_number: '',
      manufacturer: '',
      application_site: '',
      healthcare_facility: '',
      administered_by: currentUser.nome || '',
      observations: ''
    });
    
    setShowVaccineModal(false);
    setShowRegisterModal(true);
  };

  // Submit vaccination
  const handleSubmitVaccination = async (e) => {
    e.preventDefault();
    
    if (!formData.vaccination_date) {
      toast.error('Data da vacinação é obrigatória');
      return;
    }
    
    try {
      await axiosInstance.post(`/patients/${patientId}/vaccinations`, {
        vaccine_id: selectedVaccine.id,
        vaccine_name: selectedVaccine.name,
        dose_number: formData.dose_number,
        dose_name: formData.dose_name,
        vaccination_date: formData.vaccination_date,
        lot_number: formData.lot_number || null,
        manufacturer: formData.manufacturer || null,
        application_site: formData.application_site || null,
        healthcare_facility: formData.healthcare_facility || null,
        administered_by: formData.administered_by || null,
        observations: formData.observations || null
      });
      
      toast.success('Vacinação registrada com sucesso!');
      setShowRegisterModal(false);
      setSelectedVaccine(null);
      fetchData();
    } catch (error) {
      console.error('Erro ao registrar vacinação:', error);
      toast.error(error.response?.data?.detail || 'Erro ao registrar vacinação');
    }
  };

  // Export to PDF (print version)
  const handleExportPDF = () => {
    window.open(`/vaccination-card/${patientId}/print`, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
          <p className="text-lg text-muted-foreground">Carregando cartão vacinal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[hsl(210,40%,98%)] via-[hsl(175,84%,95%)] to-[hsl(210,40%,98%)] print:bg-white">
      <div className="max-w-7xl mx-auto p-6 print:p-4">
        {/* Header */}
        <div className="mb-6 print:mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div className="flex items-center gap-3">
              <Button
                onClick={() => navigate('/')}
                variant="outline"
                size="sm"
                className="rounded-full print:hidden"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Voltar
              </Button>
              <div>
                <h1 className="text-3xl font-bold tracking-tight text-foreground flex items-center gap-2">
                  <Syringe className="w-8 h-8 text-primary" />
                  Cartão Vacinal Digital
                </h1>
                <p className="text-muted-foreground">
                  {patient?.nome}
                  {patient?.data_nascimento && patient?.data_nascimento !== '01/01/1970' && (
                    <span className="ml-2">• Nascimento: {patient.data_nascimento}</span>
                  )}
                </p>
              </div>
            </div>
            <div className="flex gap-2 print:hidden">
              <Button
                onClick={() => setShowVaccineModal(true)}
                className="bg-primary hover:bg-primary/90 rounded-full"
              >
                <Plus className="w-4 h-4 mr-2" />
                Registrar Vacina
              </Button>
              <Button
                onClick={handleExportPDF}
                variant="outline"
                className="rounded-full"
              >
                <Download className="w-4 h-4 mr-2" />
                Exportar PDF
              </Button>
            </div>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6 print:grid-cols-4">
          <Card className={statusConfig.complete.color}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium opacity-75">Completas</p>
                  <p className="text-2xl font-bold">{vaccinationCard?.summary?.complete || 0}</p>
                </div>
                <CheckCircle2 className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={statusConfig.in_progress.color}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium opacity-75">Em Andamento</p>
                  <p className="text-2xl font-bold">{vaccinationCard?.summary?.in_progress || 0}</p>
                </div>
                <Clock className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={statusConfig.pending.color}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium opacity-75">Pendentes</p>
                  <p className="text-2xl font-bold">{vaccinationCard?.summary?.pending || 0}</p>
                </div>
                <XCircle className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
          <Card className={statusConfig.overdue.color}>
            <CardContent className="pt-4 pb-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium opacity-75">Atrasadas</p>
                  <p className="text-2xl font-bold">{vaccinationCard?.summary?.overdue || 0}</p>
                </div>
                <AlertCircle className="w-8 h-8 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Alerts for overdue vaccines */}
        {vaccinationCard?.overdue?.length > 0 && (
          <Card className="mb-6 border-red-200 bg-red-50 print:hidden">
            <CardHeader className="pb-2">
              <CardTitle className="text-red-800 flex items-center gap-2 text-lg">
                <AlertCircle className="w-5 h-5" />
                Vacinas Atrasadas
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-2">
                {vaccinationCard.overdue.map((vaccine, idx) => (
                  <Badge key={idx} variant="destructive" className="text-sm py-1 px-3">
                    {vaccine.vaccine_name} - {vaccine.next_dose?.dose_name}
                  </Badge>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card className="mb-6 print:hidden">
          <CardContent className="pt-4">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Buscar vacina..."
                  className="pl-10 h-10"
                />
              </div>
              <Select value={filterType} onValueChange={setFilterType}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Tipo" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os tipos</SelectItem>
                  <SelectItem value="PNI">PNI (SUS)</SelectItem>
                  <SelectItem value="SBIM">SBIM</SelectItem>
                  <SelectItem value="AMBAS">PNI e SBIM</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os status</SelectItem>
                  <SelectItem value="complete">✅ Completas</SelectItem>
                  <SelectItem value="in_progress">🔄 Em andamento</SelectItem>
                  <SelectItem value="pending">⏳ Pendentes</SelectItem>
                  <SelectItem value="overdue">⚠️ Atrasadas</SelectItem>
                </SelectContent>
              </Select>
              <Select value={filterGroup} onValueChange={setFilterGroup}>
                <SelectTrigger className="h-10">
                  <SelectValue placeholder="Faixa etária" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas as idades</SelectItem>
                  <SelectItem value="crianca">👶 Criança</SelectItem>
                  <SelectItem value="adolescente">🧒 Adolescente</SelectItem>
                  <SelectItem value="adulto">🧑 Adulto</SelectItem>
                  <SelectItem value="idoso">👴 Idoso</SelectItem>
                  <SelectItem value="gestante">🤰 Gestante</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Vaccination Card Content */}
        <Tabs defaultValue="card" className="w-full">
          <TabsList className="grid w-full grid-cols-3 bg-white border border-gray-100 rounded-lg p-1 mb-6 print:hidden">
            <TabsTrigger value="card" className="rounded-md">
              <FileText className="w-4 h-4 mr-2" />
              Cartão Vacinal
            </TabsTrigger>
            <TabsTrigger value="history" className="rounded-md">
              <Calendar className="w-4 h-4 mr-2" />
              Histórico
            </TabsTrigger>
            <TabsTrigger value="upcoming" className="rounded-md">
              <Clock className="w-4 h-4 mr-2" />
              Próximas
            </TabsTrigger>
          </TabsList>

          {/* Card View */}
          <TabsContent value="card" className="print:block">
            <div className="grid gap-3">
              {filteredStatus.map((status, idx) => {
                const StatusIcon = statusConfig[status.status]?.icon || XCircle;
                const vaccine = vaccines.find(v => v.id === status.vaccine_id);
                
                return (
                  <Card key={idx} className={`${statusConfig[status.status]?.color} transition-all hover:shadow-md`}>
                    <CardContent className="pt-4 pb-4">
                      <div className="flex items-center justify-between flex-wrap gap-4">
                        <div className="flex items-center gap-4">
                          <div className={`w-10 h-10 rounded-full ${statusConfig[status.status]?.badgeColor} flex items-center justify-center`}>
                            <StatusIcon className="w-5 h-5 text-white" />
                          </div>
                          <div>
                            <h3 className="font-semibold text-lg">{status.vaccine_name}</h3>
                            <div className="flex items-center gap-2 text-sm opacity-75">
                              <Badge variant="outline" className="text-xs">
                                {status.type}
                              </Badge>
                              <span>•</span>
                              <span>{status.doses_taken}/{status.doses_required} doses</span>
                              {status.abbreviation && (
                                <>
                                  <span>•</span>
                                  <span>{status.abbreviation}</span>
                                </>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-3">
                          <div className="text-right">
                            <Badge className={statusConfig[status.status]?.badgeColor + ' text-white'}>
                              {statusConfig[status.status]?.label}
                            </Badge>
                            {status.next_dose && status.days_until_next !== null && (
                              <p className="text-xs mt-1 opacity-75">
                                {status.days_until_next < 0 
                                  ? `Atrasada há ${Math.abs(status.days_until_next)} dias`
                                  : status.days_until_next === 0 
                                    ? 'Hoje!'
                                    : `Próxima em ${status.days_until_next} dias`
                                }
                              </p>
                            )}
                          </div>
                          {status.status !== 'complete' && (
                            <Button
                              size="sm"
                              onClick={() => vaccine && handleSelectVaccine(vaccine)}
                              className="rounded-full print:hidden"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Registrar
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* History View */}
          <TabsContent value="history">
            <Card>
              <CardHeader>
                <CardTitle>Histórico de Vacinações</CardTitle>
                <CardDescription>
                  {vaccinationCard?.records?.length || 0} registro(s) encontrado(s)
                </CardDescription>
              </CardHeader>
              <CardContent>
                {vaccinationCard?.records?.length > 0 ? (
                  <div className="space-y-3">
                    {vaccinationCard.records.map((record, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                            <CheckCircle2 className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium">{record.vaccine_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {record.dose_name} • {new Date(record.vaccination_date).toLocaleDateString('pt-BR')}
                            </p>
                          </div>
                        </div>
                        <div className="text-right text-sm text-muted-foreground">
                          {record.healthcare_facility && (
                            <p className="flex items-center gap-1 justify-end">
                              <Building2 className="w-3 h-3" />
                              {record.healthcare_facility}
                            </p>
                          )}
                          {record.administered_by && (
                            <p className="flex items-center gap-1 justify-end">
                              <Stethoscope className="w-3 h-3" />
                              {record.administered_by}
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <Syringe className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhuma vacinação registrada</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Upcoming View */}
          <TabsContent value="upcoming">
            <Card>
              <CardHeader>
                <CardTitle>Próximas Vacinas</CardTitle>
                <CardDescription>
                  Vacinas recomendadas com base no calendário vacinal
                </CardDescription>
              </CardHeader>
              <CardContent>
                {vaccinationCard?.upcoming?.length > 0 ? (
                  <div className="space-y-3">
                    {vaccinationCard.upcoming.map((vaccine, idx) => (
                      <div key={idx} className="flex items-center justify-between p-4 bg-blue-50 rounded-lg border border-blue-100">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                            <Clock className="w-5 h-5 text-blue-600" />
                          </div>
                          <div>
                            <p className="font-medium">{vaccine.vaccine_name}</p>
                            <p className="text-sm text-muted-foreground">
                              {vaccine.next_dose?.dose_name}
                            </p>
                          </div>
                        </div>
                        <div className="text-right">
                          <Badge variant="outline" className="bg-white">
                            {vaccine.days_until_next === 0 
                              ? 'Hoje!'
                              : `Em ${vaccine.days_until_next} dias`
                            }
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 text-muted-foreground">
                    <CheckCircle2 className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhuma vacina pendente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Legal Notice */}
        <div className="mt-8 p-4 bg-yellow-50 border border-yellow-200 rounded-lg text-center text-sm text-yellow-800">
          <Info className="w-4 h-4 inline mr-2" />
          {vaccinationCard?.legal_notice || "Este registro não substitui o cartão vacinal oficial do Ministério da Saúde."}
        </div>
      </div>

      {/* Vaccine Selection Modal */}
      <Dialog open={showVaccineModal} onOpenChange={setShowVaccineModal}>
        <DialogContent className="max-w-2xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Syringe className="w-5 h-5" />
              Selecionar Vacina
            </DialogTitle>
            <DialogDescription>
              Busque e selecione a vacina para registrar
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Buscar por nome, sigla ou doença..."
                className="pl-10"
              />
            </div>
            
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {filteredVaccines.map((vaccine) => {
                  const status = vaccinationCard?.vaccination_status?.find(s => s.vaccine_id === vaccine.id);
                  
                  return (
                    <div
                      key={vaccine.id}
                      onClick={() => handleSelectVaccine(vaccine)}
                      className="p-4 border rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <h4 className="font-medium">{vaccine.name}</h4>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge variant="outline" className="text-xs">
                              {vaccine.type}
                            </Badge>
                            {vaccine.abbreviation && (
                              <Badge variant="secondary" className="text-xs">
                                {vaccine.abbreviation}
                              </Badge>
                            )}
                            <span className="text-xs text-muted-foreground">
                              {vaccine.doses.length} dose(s)
                            </span>
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">
                            Previne: {vaccine.diseases_prevented.slice(0, 3).join(', ')}
                            {vaccine.diseases_prevented.length > 3 && '...'}
                          </p>
                        </div>
                        {status && (
                          <Badge className={statusConfig[status.status]?.badgeColor + ' text-white'}>
                            {status.doses_taken}/{status.doses_required}
                          </Badge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Register Vaccination Modal */}
      <Dialog open={showRegisterModal} onOpenChange={setShowRegisterModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Syringe className="w-5 h-5 text-primary" />
              Registrar Vacinação
            </DialogTitle>
            <DialogDescription>
              {selectedVaccine?.name}
            </DialogDescription>
          </DialogHeader>
          
          <form onSubmit={handleSubmitVaccination} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Data da Vacinação *</Label>
                <DateInput
                  value={formData.vaccination_date}
                  onChange={(value) => setFormData({...formData, vaccination_date: value})}
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Dose</Label>
                <Select 
                  value={String(formData.dose_number)} 
                  onValueChange={(value) => {
                    const doseNum = parseInt(value);
                    const dose = selectedVaccine?.doses?.find(d => d.dose_number === doseNum);
                    setFormData({
                      ...formData, 
                      dose_number: doseNum,
                      dose_name: dose?.dose_name || `${doseNum}ª dose`
                    });
                  }}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {selectedVaccine?.doses?.map((dose) => (
                      <SelectItem key={dose.dose_number} value={String(dose.dose_number)}>
                        {dose.dose_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Lote</Label>
                <Input
                  value={formData.lot_number}
                  onChange={(e) => setFormData({...formData, lot_number: e.target.value})}
                  placeholder="Ex: AB123456"
                  className="mt-1"
                />
              </div>
              <div>
                <Label className="text-sm">Fabricante</Label>
                <Input
                  value={formData.manufacturer}
                  onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                  placeholder="Ex: Pfizer, AstraZeneca..."
                  className="mt-1"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-sm">Local de Aplicação</Label>
                <Select 
                  value={formData.application_site} 
                  onValueChange={(value) => setFormData({...formData, application_site: value})}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Braço direito">Braço direito</SelectItem>
                    <SelectItem value="Braço esquerdo">Braço esquerdo</SelectItem>
                    <SelectItem value="Coxa direita">Coxa direita</SelectItem>
                    <SelectItem value="Coxa esquerda">Coxa esquerda</SelectItem>
                    <SelectItem value="Glúteo">Glúteo</SelectItem>
                    <SelectItem value="Via oral">Via oral</SelectItem>
                    <SelectItem value="Nasal">Nasal</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-sm">Unidade de Saúde</Label>
                <Input
                  value={formData.healthcare_facility}
                  onChange={(e) => setFormData({...formData, healthcare_facility: e.target.value})}
                  placeholder="Ex: UBS Centro, Clínica..."
                  className="mt-1"
                />
              </div>
            </div>

            <div>
              <Label className="text-sm">Profissional Aplicador</Label>
              <Input
                value={formData.administered_by}
                onChange={(e) => setFormData({...formData, administered_by: e.target.value})}
                placeholder="Nome do profissional"
                className="mt-1"
              />
            </div>

            <div>
              <Label className="text-sm">Observações</Label>
              <Textarea
                value={formData.observations}
                onChange={(e) => setFormData({...formData, observations: e.target.value})}
                placeholder="Observações adicionais..."
                className="mt-1"
                rows={3}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowRegisterModal(false)} className="rounded-full">
                Cancelar
              </Button>
              <Button type="submit" className="rounded-full">
                <Syringe className="w-4 h-4 mr-2" />
                Salvar
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

    </div>
  );
}
